import Button from "../../components/controls/Button";
// src/screens/traceability/TraceabilityScreen.jsx
import { connect } from "react-redux";
import * as actions from "../../store/actions/index";
import { withRouter } from "../../shared/utility";
import { useRef, useState } from "react";
import EmptyModal from "../../components/modals/EmptyModal";
import SelectMenu from "../../components/controls/SelectMenu";
import { TraceabilityViewModel } from "../../viewmodels/traceability/TraceabilityViewModel";
import {
  ExclamationTriangleIcon,
  MagnifyingGlassIcon,
} from "@heroicons/react/24/outline";
import ManufacturedProductTraceability from "./ManufacturedProductTraceability";
import SupplierItemTraceability from "./SupplierItemTraceability";
import { RecordLoadingSkeleton } from "../../components/shared/ContentLoadingSkeleton";

const reportTypes = [
  { value: 1, label: "Manufactured product" },
  { value: 2, label: "Supplier item" },
];

// -------------------- Screen --------------------
export function TraceabilityScreen(props) {
  const { logout } = props;

  const {
    doLookup,
    loading,
    reportLoading,
    createInspectorReport,
    emptyModal,
    setEmptyModelOpen,
    lookup,
    setLookup,
    err,
    setErr,
    currentWip,
    setSelectedPlu,
    loadPluDetails,
    loadDirectParents,
    selectedPlu,
    incomingPlus,
    byParent,
    byLot,
    getSalesReport,
    // Supplier item props (Trace Children)
    lotNumber,
    source,
    directChildren,
    loadDirectChildren,
    // Credit return tracing
    creditReturn,
    creditReturnItems,
  } = TraceabilityViewModel(logout);

  const searchRef = useRef();
  const [reportType, setReportType] = useState(reportTypes[0]);
  const [successfulLookup, setSuccessfulLookup] = useState("");

  // Search
  const onSearch = async () => {
    const val = (searchRef.current?.value || "").trim();
    if (!val) return;
    setSuccessfulLookup("");
    setSelectedPlu(null);
    setLookup(val);
    const result = await doLookup(val, reportType.value);
    setSuccessfulLookup(result?.ok ? val : "");
  };

  return (
    <div className="mt-2 lg:px-8 space-y-4">
      {/* Header / search */}
      <div className="sm:flex sm:items-center px-4 py-5 card-custom">
        <div className="sm:flex-auto w-full">
          <h1 className="text-base font-semibold leading-6 text-gray-900">
            Traceability
          </h1>

          <div className="mt-5 flex flex-wrap items-center gap-3">
            <div className="relative flex items-stretch flex-grow min-w-[280px]">
              <input
                type="search"
                className="block w-full rounded-l-md pl-2 sm:text-sm border border-gray-300"
                placeholder={
                  reportType?.value === 1
                    ? "Search by shipping barcode, lot or 10-digit serial number"
                    : "Search by receiving lot number, invoice number, or barcode"
                }
                ref={searchRef}
                defaultValue={lookup}
                disabled={loading}
                onChange={(event) => {
                  if (event.target.value.trim() !== successfulLookup) {
                    setSuccessfulLookup("");
                  }
                }}
                onKeyDown={(e) => e.key === "Enter" && onSearch()}
              />
              <button
                type="button"
                className="-ml-px inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-r-md text-gray-700 bg-gray-50 hover:bg-gray-100 focus:outline-none focus:ring-1"
                onClick={onSearch}
                disabled={loading}
              >
                <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
              </button>
            </div>

            <SelectMenu
              handleInputChange={(e) => {
                setReportType(e.target.value);
                // Reset state when switching report types
                setLookup("");
                setSuccessfulLookup("");
                setErr("");
                searchRef.current.value = "";
                setSelectedPlu(null);
              }}
              name="report_type"
              options={reportTypes}
              parentClass="rounded-md w-56"
              value={reportType}
              disabled={loading}
            />

            <Button
              variant="success"
              onClick={() =>
                createInspectorReport(successfulLookup, reportType?.value)
              }
              disabled={!successfulLookup || loading || reportLoading}
            >
              {reportLoading ? "Creating report..." : "Create inspector report"}
            </Button>
          </div>

          {err ? (
            <div
              className="mt-3 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
              role="alert"
            >
              <ExclamationTriangleIcon
                className="mt-0.5 h-5 w-5 shrink-0"
                aria-hidden="true"
              />
              <span>{err}</span>
            </div>
          ) : null}
        </div>
      </div>

      {/* Conditionally render based on report type */}
      {loading ? (
        <RecordLoadingSkeleton className="px-4" />
      ) : reportType?.value === 1 ? (
        <ManufacturedProductTraceability
          lookup={lookup}
          creditReturn={creditReturn}
          creditReturnItems={creditReturnItems}
          currentWip={currentWip}
          incomingPlus={incomingPlus}
          selectedPlu={selectedPlu}
          setSelectedPlu={setSelectedPlu}
          loadPluDetails={loadPluDetails}
          loadDirectParents={loadDirectParents}
          byParent={byParent}
          byLot={byLot}
          getSalesReport={getSalesReport}
        />
      ) : reportType?.value === 2 ? (
        <SupplierItemTraceability
          lookup={lookup}
          loading={loading}
          error={err}
          lotNumber={lotNumber}
          source={source}
          directChildren={directChildren}
          loadDirectChildren={loadDirectChildren}
        />
      ) : null}

      <EmptyModal
        childComponent={emptyModal.childComponent}
        open={emptyModal.open}
        setOpen={setEmptyModelOpen}
        size={emptyModal.size}
      />
    </div>
  );
}

const mapStateToProps = () => ({});
const mapDispatchToProps = (dispatch) => ({
  logout: () => dispatch(actions.logout()),
});

export default withRouter(
  connect(mapStateToProps, mapDispatchToProps)(TraceabilityScreen),
);
